import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import StepIndicator from "../components/StepIndicator";
import AuthGate from "../components/AuthGate";

const analyzeSteps = [
    { number: 1, label: 'Dónde y quién eres', path: '/analizarpropiedad/:id/paso1' },
    { number: 2, label: 'Imágenes', path: '/analizarpropiedad/:id/imagenes' },
    { number: 3, label: 'Información básica', path: '/analizarpropiedad/:id/datos' },
    { number: 4, label: 'Recibir informe', path: '/analizarpropiedad/:id/informe' }
];

const AnalyzePropertyReport = () => {
    const { id } = useParams(); // UUID
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showAuthGate, setShowAuthGate] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [numericId, setNumericId] = useState(null);
    const [informeReady, setInformeReady] = useState(false);
    const [informeError, setInformeError] = useState(null);
    const [informeData, setInformeData] = useState(null);

    const prepararInforme = useCallback(async (propId) => {
        setLoading(true);
        setInformeError(null);
        try {
            const token = searchParams.get("token");
            const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";

            // Llamada al endpoint v2 (nuevo pipeline TS)
            const v2Url = `${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${propId}/preparar${tokenParam}`;
            const v2Res = await fetch(v2Url, { method: "POST", credentials: "include" });
            if (!v2Res.ok) throw new Error("Error al preparar el informe");

            // Obtener informe estructurado
            const informeRes = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${propId}/informe`,
                { credentials: "include" }
            );
            if (informeRes.ok) {
                const data = await informeRes.json();
                setInformeData(data);
            }

            // Lanzar análisis IA y esperar — la pantalla de carga ya indica que puede tardar
            try {
                await fetch(
                    `${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${propId}/analisis-ia`,
                    { method: "POST", credentials: "include" }
                );
                // Re-fetch informe para incluir escenariosIA actualizados
                const informeIA = await fetch(
                    `${window.REACT_APP_API_ENDPOINT}/api/v2/propiedades/${propId}/informe`,
                    { credentials: "include" }
                );
                if (informeIA.ok) {
                    const dataIA = await informeIA.json();
                    setInformeData(dataIA);
                }
            } catch (_) {
                // El análisis IA falló — el informe se mostrará sin datos IA
            }

            setInformeReady(true);
        } catch (err) {
            setInformeError("No se pudo preparar el informe. Inténtalo de nuevo.");
        } finally {
            setLoading(false);
        }
    }, [searchParams]);

    // Rotating loading messages
    const mensajesCarga = [
        "Leyendo las imágenes de la propiedad...",
        "Identificando el estado de cada estancia...",
        "Detectando materiales y acabados...",
        "Evaluando carpintería y ventanas...",
        "Analizando instalaciones eléctricas y fontanería...",
        "Calculando actuaciones de reforma necesarias...",
        "Estimando costes por constructor...",
        "Ajustando cantidades según superficie y distribución...",
        "Generando el resumen del estado general...",
        "Preparando tu informe personalizado...",
    ];
    const [mensajeCargaIdx, setMensajeCargaIdx] = useState(0);
    useEffect(() => {
        if (!loading) return;
        const interval = setInterval(() => {
            setMensajeCargaIdx(i => (i + 1) % mensajesCarga.length);
        }, 3500);
        return () => clearInterval(interval);
    }, [loading]);

    // OTP state
    const [otpRequired, setOtpRequired] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [otpAccessId, setOtpAccessId] = useState(null);
    const [otpEmail, setOtpEmail] = useState("");
    const [otpError, setOtpError] = useState(null);
    const [otpLoading, setOtpLoading] = useState(false);

    // Resolve UUID to numeric id
    useEffect(() => {
        const resolveId = async () => {
            const isUuid = /^[a-f0-9-]{36}$/.test(id);
            if (isUuid) {
                try {
                    const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/by-public-id/${id}`, {
                        credentials: 'include'
                    });
                    if (res.ok) {
                        const data = await res.json();
                        setNumericId(data.id);
                    }
                } catch (err) {
                    console.error('Error resolving public_id:', err);
                }
            } else {
                setNumericId(id);
            }
        };
        resolveId();
    }, [id]);

    useEffect(() => {
        if (!numericId) return;

        const checkAccess = async () => {
            // 1. Check for token in URL
            const token = searchParams.get("token");
            if (token && numericId) {
                try {
                    const res = await fetch(
                        `${window.REACT_APP_API_ENDPOINT}/auth/validate-token?token=${encodeURIComponent(token)}&propiedadId=${numericId}`,
                        { credentials: "include" }
                    );
                    if (res.ok) {
                        const data = await res.json();

                        // Already verified — prepare report
                        if (data.verified) {
                            setAuthenticated(true);
                            prepararInforme(numericId);
                            return;
                        }

                        // OTP required — show OTP input
                        if (data.otpRequired) {
                            setOtpRequired(true);
                            setOtpAccessId(data.accessId);
                            setOtpEmail(data.email);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (err) {
                    console.error("Error validating token:", err);
                }
            }

            // 2. Check if user is authenticated
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/users/current-user`, {
                    credentials: "include",
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.user) {
                        setAuthenticated(true);
                        prepararInforme(numericId);
                        return;
                    }
                }
            } catch (err) {
                console.error("Error checking auth:", err);
            }

            // 3. Not authenticated — show auth gate
            setLoading(false);
            setShowAuthGate(true);
        };

        checkAccess();
    }, [numericId, searchParams, prepararInforme]);

    const handleAuthSuccess = () => {
        setShowAuthGate(false);
        setAuthenticated(true);
        prepararInforme(numericId);
    };

    const handleContinueWithEmail = (email, token) => {
        setShowAuthGate(false);
        setEmailSent(true);
        setAuthenticated(true);
        prepararInforme(numericId);
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setOtpError(null);
        setOtpLoading(true);

        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ accessId: otpAccessId, code: otpCode }),
            });

            const data = await res.json();
            if (!res.ok) {
                setOtpError(data.error || "Código incorrecto");
                return;
            }

            // OTP verified — prepare report
            setOtpRequired(false);
            setAuthenticated(true);
            prepararInforme(numericId);
        } catch (err) {
            setOtpError("Error al verificar el código");
        } finally {
            setOtpLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
                <StepIndicator currentStep={4} steps={analyzeSteps} propertyId={id} />

                {otpRequired ? (
                    <div className="py-8 max-w-md mx-auto">
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-100 rounded-full mb-4">
                                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">Verificación requerida</h2>
                            <p className="text-gray-500 text-sm">
                                Hemos enviado un código de 6 dígitos a <strong>{otpEmail}</strong>.
                                Introdúcelo a continuación para acceder al informe.
                            </p>
                        </div>

                        {otpError && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4 text-sm text-center">{otpError}</div>
                        )}

                        <form onSubmit={handleOtpSubmit} className="space-y-4">
                            <div>
                                <input
                                    type="text"
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    className="w-full text-center text-2xl tracking-[0.5em] px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="000000"
                                    autoFocus
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={otpLoading || otpCode.length !== 6}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm disabled:opacity-50"
                            >
                                {otpLoading ? "Verificando..." : "Verificar código"}
                            </button>
                        </form>
                    </div>
                ) : showAuthGate ? (
                    <AuthGate
                        propertyId={numericId}
                        onAuthSuccess={handleAuthSuccess}
                        onContinueWithEmail={handleContinueWithEmail}
                    />
                ) : loading ? (
                    <div className="text-center py-16">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-6"></div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            Analizando tu propiedad...
                        </h2>
                        <p className="text-blue-600 font-medium text-sm mb-4 h-5 transition-all duration-500">
                            {mensajesCarga[mensajeCargaIdx]}
                        </p>
                        <p className="text-gray-400 text-xs">
                            Esto puede tardar entre 30 y 60 segundos. Por favor, no cierres esta página.
                        </p>
                    </div>
                ) : authenticated && informeReady ? (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Informe generado
                        </h2>
                        <p className="text-gray-500 mb-6">
                            Tu informe de análisis de reforma está listo.
                        </p>
                        {emailSent && (
                            <p className="text-sm text-green-600 mb-6">
                                También hemos enviado un enlace a tu email para acceder al informe en el futuro.
                            </p>
                        )}
                        <button
                            onClick={() => {
                                const token = searchParams.get("token");
                                const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
                                navigate(`/analizarpropiedad/${id}/informe-detalle${tokenParam}`);
                            }}
                            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors"
                        >
                            Ver informe completo
                            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>
                ) : authenticated && informeError ? (
                    <div className="text-center py-16">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
                        <p className="text-gray-500 mb-4">{informeError}</p>
                        <button
                            onClick={() => prepararInforme(numericId)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                        >
                            Reintentar
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default AnalyzePropertyReport;
